import { action, computed, observable } from 'mobx';
import * as moment from 'moment';
import {
  ExperienceStateType,
  ICalculation,
  IExperience,
  IPageTag,
  IRoundInfo,
  PortfolioReportItem,
  RoundPurposeType,
  TargetDeviceType,
} from '../entity/PortfolioReportItem';
const pull = require('lodash.pull');

export class PortfolioReportStore {
  public static deserializeFromApiResponse(res: any): PortfolioReportItem {
    const goalId = res.goal_id;
    const from = moment(res.from).toDate();
    const to = moment(res.to).toDate();
    const calculations: ICalculation[] = res.calculations.map((calculation: any) => {
      const pageTag: IPageTag = calculation.project.page_tag
        ? {
            id: calculation.project.page_tag.id,
            name: calculation.project.page_tag.name,
            ja: calculation.project.page_tag.ja,
            order: calculation.project.page_tag.sort_order,
          }
        : {
            id: null,
            name: '',
            ja: '',
            order: 999,
          };
      const rounds: IRoundInfo[] = calculation.rounds
        .filter((round: any) => {
          return round.type.match(/^(initial|competition|interim)$/);
        })
        .filter((round: any) => {
          return moment(round.ended_at).diff(moment(round.started_at), 'days') > 1;
        })
        .map((round: any): IRoundInfo => {
          return {
            id: round.id,
            num: round.num,
            type: round.type !== 'initial' && round.purpose_type === 'original' ? round.purpose_type : round.type,
            startedAt: moment(round.started_at).toDate(),
            endedAt: moment(round.ended_at).toDate(),
            purposeType: round.type === 'initial' ? round.type : round.purpose_type,
            note: round.note.body.replace(/\n/, '<br />'),
            resultNote: round.result_note.body.replace(/\n/g, '<br />'),
            improvementRatio: round.kaizen_ratio * 100,
            roundState: {
              id: round.round_states[round.round_states.length - 1].id,
              type: round.round_states[round.round_states.length - 1].type,
            },
          };
        });
      const experience: IExperience = {
        id: calculation.project.id,
        title: calculation.project.title,
        url: calculation.project.url,
        state: calculation.project.state,
        createdAt: moment(calculation.project.created_at).toDate(),
        pageTag,
        rounds,
        cvCoverageRatio: calculation.cv_coverage_ratio * 100,
        totalImprovementRatio: calculation.kaizen_ratio * 100,
        actualAveragePv: calculation.average_pv,
        actualAverageCv: calculation.average_cv,
        actualAverageCvr: calculation.average_cvr * 100,
        targetDevice: calculation.project.target_device,
        hasAllCv: calculation.has_all_cv,
      };
      return {
        experience,
      };
    });
    return new PortfolioReportItem({ goalId, from, to, calculations });
  }

  @observable public portfolioReport: PortfolioReportItem;
  @observable public isLoading: boolean = true;
  @observable public targetDeviceFilters: TargetDeviceType[] = [];
  @observable public stateFilters: ExperienceStateType[] = [];
  @observable public roundPurposeFilters: RoundPurposeType[] = [];

  @computed
  get experienceList(): IExperience[] {
    let experienceList = this.portfolioReport.calculations
      .map((calculation: ICalculation) => calculation.experience)
      .sort(
        (a: IExperience, b: IExperience) =>
          (a.pageTag.order === b.pageTag.order ? 0 : a.pageTag.order > b.pageTag.order ? 1 : -1) ||
          (a.url === b.url ? 0 : a.url > b.url ? 1 : -1) ||
          (a.id > b.id ? 1 : -1),
      );
    if (this.targetDeviceFilters.length > 0) {
      experienceList = experienceList.filter((exp: IExperience) => {
        return this.targetDeviceFilters.includes(exp.targetDevice);
      });
    }
    if (this.stateFilters.length > 0) {
      experienceList = experienceList.filter((exp: IExperience) => {
        return this.stateFilters.includes(exp.state);
      });
    }
    if (this.roundPurposeFilters.length > 0) {
      experienceList = experienceList.filter((exp: IExperience) => {
        const currentRound = exp.rounds[exp.rounds.length - 1];
        return (
          exp.state === 'competition' && currentRound && this.roundPurposeFilters.includes(currentRound.purposeType)
        );
      });
    }
    return experienceList;
  }

  @computed
  get hasFilters(): boolean {
    return this.targetDeviceFilters.length > 0 || this.stateFilters.length > 0 || this.roundPurposeFilters.length > 0;
  }

  @action
  public setPortfolioReport(res: any): void {
    this.portfolioReport = PortfolioReportStore.deserializeFromApiResponse(res);
  }

  @action
  public setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @action
  public setTargetDeviceFilter(targetDeviceFilter: TargetDeviceType): void {
    if (!this.targetDeviceFilters.includes(targetDeviceFilter)) {
      this.targetDeviceFilters.push(targetDeviceFilter);
    }
  }

  @action
  public unsetTargetDeviceFilter(targetDeviceFilter: TargetDeviceType): void {
    if (this.targetDeviceFilters.includes(targetDeviceFilter)) {
      pull(this.targetDeviceFilters, targetDeviceFilter);
    }
  }

  @action
  public setStateFilter(stateFilter: ExperienceStateType): void {
    if (!this.stateFilters.includes(stateFilter)) {
      this.stateFilters.push(stateFilter);
    }
  }

  @action
  public unsetStateFilter(stateFilter: ExperienceStateType): void {
    if (this.stateFilters.includes(stateFilter)) {
      pull(this.stateFilters, stateFilter);
    }
  }

  @action
  public setRoundPurposeFilter(roundPurposeFilter: RoundPurposeType): void {
    if (!this.roundPurposeFilters.includes(roundPurposeFilter)) {
      this.roundPurposeFilters.push(roundPurposeFilter);
    }
  }

  @action
  public unsetRoundPurposeFilter(roundPurposeFilter: RoundPurposeType): void {
    if (this.roundPurposeFilters.includes(roundPurposeFilter)) {
      pull(this.roundPurposeFilters, roundPurposeFilter);
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/domain/store/PortfolioReportStore.ts